import { useQuery } from '@tanstack/react-query';

import { getTaskArtifacts } from '@/lib/api/generated/api';
import type { TaskArtifactsView } from '@/lib/api/generated/model';
import { LIVE_POLL_MS } from '../liveCadence';
import { taskKeys } from '../queryKeys';
import type { UseTaskResultsOptions } from './useTaskResults';

/**
 * What the task created outside the platform — merge requests, pull requests
 * and branches, each once, as the forge reported them (spec 119, service spec
 * 112).
 *
 * The results read's twin, under the same rules (FR-014): read once when the
 * drawer opens, polled only by the drawer and only while Results is shown and a
 * run is live, never stale once every run has ended. A separate query rather
 * than a field on the results read, so a failure here costs the section and
 * nothing else (FR-015, research R1).
 */
export function useTaskArtifacts(
  workspaceId: string,
  taskId: string,
  { enabled, poll, live }: UseTaskResultsOptions,
) {
  const query = useQuery({
    queryKey: taskKeys.artifacts(workspaceId, taskId),
    queryFn: async (): Promise<TaskArtifactsView> => {
      const response = await getTaskArtifacts(workspaceId, taskId);
      if (response.status !== 200) {
        throw new Error('What this task created could not be loaded.');
      }
      return response.data;
    },
    enabled,
    refetchInterval: poll ? LIVE_POLL_MS : false,
    staleTime: live ? 0 : Infinity,
    retry: false,
  });

  return {
    artifacts: query.data,
    isPending: query.isPending,
    loadFailed: query.isError,
    retry: () => void query.refetch(),
  };
}
